/**
 * Tab ledger helpers aligned with TabService::computeBalance (blu-backend).
 */

export function tabCreditMinorFromCurrentTotal (currentTotalMinor) {
  if (typeof currentTotalMinor === 'number' && currentTotalMinor < 0) {
    return Math.abs(currentTotalMinor)
  }
  return 0
}

/**
 * Credit available on the tab (prepay or post-payment store credit).
 * Prefer API `credit_on_tab_minor`; fall back to negative current total for older responses.
 */
export function tabCreditOnTabMinorFromSummary (summary) {
  if (typeof summary?.credit_on_tab_minor === 'number' && summary.credit_on_tab_minor > 0) {
    return summary.credit_on_tab_minor
  }
  return tabCreditMinorFromCurrentTotal(summary?.current_total_minor)
}

/** @deprecated Use tabCreditOnTabMinorFromSummary */
export function tabCreditMinorFromSummary (summary) {
  return tabCreditOnTabMinorFromSummary(summary)
}

export function formatMinorCurrency (minor, currency = 'USD') {
  const val = (Number(minor) || 0) / 100
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD'
  }).format(val)
}

/**
 * Ledger current total: negative values render as green "Cr $x.xx".
 *
 * @param {number|null|undefined} minor
 * @param {string} currency
 * @param {(key: string, params?: object) => string} t
 */
export function tabLedgerDisplay (minor, currency, t) {
  const creditMinor = tabCreditMinorFromCurrentTotal(minor)
  if (creditMinor > 0) {
    return {
      text: t('tabs.creditDisplay', { amount: formatMinorCurrency(creditMinor, currency) }),
      isCredit: true
    }
  }
  return {
    text: formatMinorCurrency(minor ?? 0, currency),
    isCredit: false
  }
}

/**
 * Positive store-credit / prepay display (Cr $x.xx).
 */
export function tabCreditOnTabDisplay (minor, currency, t) {
  const creditMinor = Math.max(0, Number(minor) || 0)
  if (creditMinor <= 0) {
    return {
      text: formatMinorCurrency(0, currency),
      isCredit: false
    }
  }
  return {
    text: t('tabs.creditDisplay', { amount: formatMinorCurrency(creditMinor, currency) }),
    isCredit: true
  }
}

/**
 * Amount owed (balance_minor): standard currency, never negative.
 */
export function tabBalanceDisplay (minor, currency) {
  return {
    text: formatMinorCurrency(Math.max(0, Number(minor) || 0), currency),
    isCredit: false
  }
}

/**
 * Primary balance line for tab summary: amount due, or credit on tab when fully paid with credit.
 */
export function tabSummaryBalanceDisplay (summary, currency, t) {
  const balanceDue = Math.max(0, Number(summary?.balance_minor) || 0)
  const creditOnTab = tabCreditOnTabMinorFromSummary(summary)

  if (balanceDue > 0) {
    return {
      labelKey: 'tabs.balanceLabel',
      ...tabBalanceDisplay(balanceDue, currency),
      showAsCredit: false
    }
  }
  if (creditOnTab > 0) {
    return {
      labelKey: 'tabs.creditOnTabLabel',
      ...tabCreditOnTabDisplay(creditOnTab, currency, t),
      showAsCredit: true
    }
  }
  return {
    labelKey: 'tabs.balanceLabel',
    ...tabBalanceDisplay(0, currency),
    showAsCredit: false
  }
}

/**
 * One-line summary for tooltips / selects (matches register tab preview).
 */
export function tabSummaryLine (summary, currency, t) {
  const balanceDue = Math.max(0, Number(summary?.balance_minor) || 0)
  const creditOnTab = tabCreditOnTabMinorFromSummary(summary)

  if (balanceDue > 0) {
    return t('sales.tabLineBalanceOnly', {
      balance: formatMinorCurrency(balanceDue, currency)
    })
  }
  if (creditOnTab > 0) {
    const creditStr = t('tabs.creditDisplay', { amount: formatMinorCurrency(creditOnTab, currency) })
    return t('sales.tabLineCreditOnly', { credit: creditStr })
  }
  return t('sales.tabLineBalanceOnly', { balance: formatMinorCurrency(0, currency) })
}
